import type { TFunction } from 'i18next';
import type {
  Initiative,
  InitiativeBreakdownDataPoint,
} from '../../../../../api/initiative-client/initiative-client.type';
import { graphChefchaouenBlue } from '../../../../../styles/design-tokens';
import { TreeMapData, TreeMapDataType } from '../main-section.type';

/**
 * Percentage threshold for initiative visibility in the tree map.
 * Initiatives with a percentage below this threshold (5%) will be grouped into the "Other" category.
 * This improves visualization clarity by preventing the tree map from becoming cluttered with tiny segments
 * that would be difficult to interact with and visually interpret.
 */
const THRESHOLD = 5;

/**
 * Minimum size (in percentage) for individual initiative cells in the tree map.
 * Initiatives with sizes between THRESHOLD and MIN_CELL_SIZE will be artificially
 * increased to this size to ensure they remain clickable and visible.
 */
const MIN_CELL_SIZE = 8;

/**
 * Calculates tree map data for visualization based on initiative breakdown data.
 *
 * This function processes initiative data to create a tree map visualization structure.
 * It separates initiatives into two categories:
 * 1. Visible initiatives - those with percentage values above the defined threshold
 * 2. "Other" initiatives - those with percentage values below the threshold
 *
 * The function:
 * - Identifies which initiatives should be displayed individually vs grouped as "Other"
 * - Creates data objects for visible initiatives with their respective sizes and metadata
 * - Aggregates smaller initiatives into a single "Other" category
 * - Sorts visible initiatives by size (largest first)
 * - Combines both sets into a single array suitable for tree map visualization
 *
 * @param {Record<string, InitiativeBreakdownDataPoint>} dataset - Initiative breakdown data keyed by initiative ID
 * @param {Initiative[]} initiatives - Array of initiative objects with complete initiative information
 * @returns {TreeMapData[]} Processed data array ready for tree map visualization
 */
const calculateTreeMapData = (
  dataset: Record<string, InitiativeBreakdownDataPoint>,
  initiatives: Initiative[],
  t: TFunction,
): TreeMapData[] => {
  const datasetEntries = Object.entries(dataset);

  const visibleInitiativeIds = datasetEntries
    .filter(([_, { percentage }]) => percentage >= THRESHOLD)
    .map(([initiativeId]) => initiativeId);

  const otherInitiativeIds = datasetEntries
    .filter(([initiativeId]) => !visibleInitiativeIds.includes(initiativeId))
    .map(([initiativeId]) => initiativeId);

  const visibleInitiativesData = initiatives
    .filter((initiative) => visibleInitiativeIds.includes(initiative.id))
    .map((initiative) => {
      let size = dataset[initiative.id]?.percentage ?? 0;
      // Artificially increase size if between threshold and min cell size
      if (size >= THRESHOLD && size < MIN_CELL_SIZE) {
        size = MIN_CELL_SIZE;
      }
      return {
        type: TreeMapDataType.Initiative,
        name: initiative.id,
        size,
        actualSize: dataset[initiative.id]?.percentage ?? 0,
        initiativeId: initiative.id,
        initiativeName: initiative.name,
        initiativeKey: dataset[initiative.id]?.key ?? null,
        fill: graphChefchaouenBlue,
      };
    })
    .sort((a, b) => b.size - a.size);

  const otherInitiativesData = datasetEntries
    .filter(([initiativeId, _]) => otherInitiativeIds.includes(initiativeId))
    .reduce(
      (acc, [initiativeId, { key, percentage }]) => {
        if (!acc.actualSize) {
          acc.actualSize = percentage;
        } else {
          acc.actualSize += percentage;
        }
        acc.otherInitiativesData[initiativeId] = { key, percentage };
        return acc;
      },
      { actualSize: 0, otherInitiativesData: {} as Record<string, InitiativeBreakdownDataPoint> },
    );

  return combineInitiativeData(
    visibleInitiativesData,
    otherInitiativesData.actualSize > 0 ? otherInitiativesData : null,
    t,
  );
};

/**
 * Combines visible initiative data with aggregated "Other" initiatives data
 *
 * This function takes the array of visible initiatives and optionally combines it with
 * an aggregated "Other" category that represents all initiatives below the visibility threshold.
 *
 * @param {TreeMapData[]} initiativeData - Array of tree map data for visible initiatives
 * @param {{ size: number; otherInitiativesData: Record<string, InitiativeBreakdownDataPoint> } | null} otherInitiativeData -
 *        Aggregated data for initiatives below threshold, or null if none exist
 * @returns {TreeMapData[]} Combined array of tree map data including both visible initiatives and "Other" category
 */
const combineInitiativeData = (
  initiativeData: TreeMapData[],
  otherInitiativeData: {
    actualSize: number;
    otherInitiativesData: Record<string, InitiativeBreakdownDataPoint>;
  } | null,
  t: TFunction,
): TreeMapData[] => {
  if (otherInitiativeData) {
    return [
      ...initiativeData,
      {
        ...otherInitiativeData,
        size: MIN_CELL_SIZE,
        type: TreeMapDataType.Other,
        name: t('strategy.initiatives.other'),
        initiativeKey: t('strategy.initiatives.other'),
        fill: graphChefchaouenBlue,
      },
    ];
  }

  return initiativeData;
};

export { calculateTreeMapData };
